import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Shield, CreditCard, QrCode, Copy, Clock, CheckCircle, Sparkles, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

type PaymentMethod = 'PIX' | 'CREDIT_CARD' | null;

interface PixQrCodeData {
  id: string;
  encodedImage: string;
  payload: string;
  expirationDate: string;
  description: string;
  isTestMode?: boolean;
}

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPixGenerate: () => Promise<void>;
  onPaymentMethodSelect: (method: PaymentMethod) => void;
  planName: string;
  planValue: number;
  isLoading?: boolean;
  pixData?: PixQrCodeData | null;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatTimeLeft = (expirationDate: string): string => {
  const now = new Date();
  const expiry = new Date(expirationDate);
  const diff = expiry.getTime() - now.getTime();
  
  if (diff <= 0) return '00:00';
  
  const minutes = Math.floor(diff / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onPixGenerate,
  onPaymentMethodSelect,
  planName,
  planValue,
  isLoading = false,
  pixData
}) => {
  const { user } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(null);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [upgradedPlan, setUpgradedPlan] = useState<string>('');
  const [copied, setCopied] = useState(false);
  
  const isTestMode = pixData?.isTestMode ?? false;

  // Timer for PIX expiration
  useEffect(() => {
    if (!pixData?.expirationDate) return;

    const timer = setInterval(() => {
      setTimeLeft(formatTimeLeft(pixData.expirationDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [pixData?.expirationDate]);

  // Real-time subscription to monitor plan changes
  useEffect(() => {
    if (!user?.id || !isOpen || !pixData) return;

    // Get current plan to compare
    const getCurrentPlan = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('plan_type')
        .eq('id', user.id)
        .single();
      return data?.plan_type;
    };

    let initialPlanType: string | null = null;

    // Store initial plan type
    getCurrentPlan().then(planType => {
      initialPlanType = planType;
    });

    // Subscribe to plan changes
    const channel = supabase
      .channel(`plan-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          const newPlanType = payload.new?.plan_type;
          
          // Check if plan changed from free to paid
          if (initialPlanType && newPlanType && 
              (initialPlanType.includes('_free') && !newPlanType.includes('_free'))) {
            console.log('[PaymentModal] Plan upgrade detected:', { from: initialPlanType, to: newPlanType });
            setUpgradedPlan(newPlanType);
            setPaymentProcessed(true);
            toast.success('Pagamento processado com sucesso! 🎉');
            
            // Auto-close modal and refresh page after 3 seconds
            setTimeout(() => {
              onClose();
              window.location.reload();
            }, 3000);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, isOpen, pixData]);

  const handleMethodSelect = async (method: PaymentMethod) => {
    setSelectedMethod(method);
    onPaymentMethodSelect(method);
    
    if (method === 'PIX') {
      try {
        await onPixGenerate();
      } catch (error) {
        console.error('Error generating PIX:', error);
        toast.error('Erro ao gerar código PIX');
        setSelectedMethod(null);
      }
    }
  };

  const handleCopyPixCode = () => {
    if (pixData?.payload) {
      navigator.clipboard.writeText(pixData.payload);
      setCopied(true);
      toast.success('Código PIX copiado!');
      
      // Reset feedback após 2 segundos
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedMethod(null);
      setPaymentProcessed(false);
      setUpgradedPlan('');
      onClose();
    }
  };

  const handleEnjoyBenefits = () => {
    setPaymentProcessed(false);
    setUpgradedPlan('');
    setSelectedMethod(null);
    onClose();
    toast.success('Bem-vindo ao seu novo plano! 🚀');
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedMethod(null);
      setPaymentProcessed(false);
      setUpgradedPlan('');
    }
  }, [isOpen]);

  const isExpired = timeLeft === '00:00' && pixData;

  const getPlanDisplayName = (planType: string) => {
    const planNames: Record<string, string> = {
      'sponsor_standard': 'Sponsor Standard',
      'sponsor_premium': 'Sponsor Premium', 
      'sponsored_premium': 'Sponsored Premium'
    };
    return planNames[planType] || planType;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-sm max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black border border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {paymentProcessed ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-400" />
                Pagamento Processado!
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 text-luxury-gold" />
                Escolher Método de Pagamento
              </>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {paymentProcessed 
              ? `Pagamento do plano ${planName} processado com sucesso`
              : `Selecione o método de pagamento para assinar o plano ${planName}`
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {paymentProcessed ? (
            // Success Screen
            <div className="text-center space-y-4 py-4">
              <div className="flex justify-center">
                <div className="relative">
                  <CheckCircle className="w-16 h-16 text-green-400 animate-pulse" />
                  <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-1 -right-1 animate-bounce" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-white">
                  Pagamento Aprovado!
                </h3>
                <p className="text-green-400 font-medium">
                  Plano {getPlanDisplayName(upgradedPlan)} ativado
                </p>
                <p className="text-white/70 text-sm">
                  Seu upgrade foi processado com sucesso. Agora você pode aproveitar todos os benefícios do seu novo plano!
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 p-4 rounded-lg border border-green-400/20">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="font-medium">Benefícios Liberados:</span>
                </div>
                <ul className="text-white/80 text-sm space-y-1">
                  <li>• Mensagens ilimitadas</li>
                  <li>• Matches ilimitados</li>
                  <li>• Perfil em destaque</li>
                  <li>• Suporte premium</li>
                </ul>
              </div>

              <Button
                onClick={handleEnjoyBenefits}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-medium py-3"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Aproveitar Benefícios Agora
              </Button>
            </div>
          ) : (
            // Original Payment Flow
            <>
              <Alert className="bg-blue-900/30 border-blue-400/30">
                <AlertCircle className="h-4 w-4 text-blue-400" />
                <AlertDescription className="text-blue-200">
                  <strong>{planName}</strong> - {formatCurrency(planValue)}
                </AlertDescription>
              </Alert>

              {!pixData && (
                <div className="space-y-4">
                  {/* Payment Method Selection */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleMethodSelect('PIX')}
                        disabled={isLoading}
                        className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          selectedMethod === 'PIX' 
                            ? 'border-green-500 bg-green-500/20 text-green-300' 
                            : 'border-white/20 text-white hover:border-white/40 disabled:opacity-50'
                        }`}
                      >
                        <QrCode className="w-5 h-5" />
                        <span className="font-medium text-sm">PIX</span>
                        <span className="text-xs opacity-75">Instantâneo</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => handleMethodSelect('CREDIT_CARD')}
                        disabled={true}
                        className="p-3 rounded-lg border-2 border-white/10 text-white/40 flex flex-col items-center gap-2 cursor-not-allowed"
                      >
                        <CreditCard className="w-5 h-5" />
                        <span className="font-medium text-sm">Cartão</span>
                        <span className="text-xs">Em breve</span>
                      </button>
                    </div>
                  </div>

                  {selectedMethod === 'CREDIT_CARD' && (
                    <Alert className="bg-yellow-900/20 border-yellow-400/30">
                      <AlertCircle className="h-4 w-4 text-yellow-400" />
                      <AlertDescription className="text-yellow-200">
                        O pagamento por cartão estará disponível em breve. Use PIX para pagamento instantâneo.
                      </AlertDescription>
                    </Alert>
                  )}

                  {isLoading && selectedMethod === 'PIX' && (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400 mx-auto"></div>
                      <p className="text-white/60 mt-2">Gerando código PIX...</p>
                    </div>
                  )}
                </div>
              )}

              {pixData && !isExpired && (
                <div className="space-y-3">
                  {/* Banner de Ambiente de Teste */}
                  {isTestMode && (
                    <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-yellow-400">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium text-sm">Ambiente de Teste (Sandbox)</span>
                      </div>
                      <p className="text-yellow-200/80 text-xs mt-1">
                        Este PIX não será cobrado. Para produção, acesse interlaced.app
                      </p>
                    </div>
                  )}
                  
                  {/* PIX QR Code Display */}
                  <div className="text-center space-y-3">
                    <div className="bg-white p-3 rounded-lg inline-block relative">
                      <img 
                        src={`data:image/png;base64,${pixData.encodedImage}`}
                        alt="QR Code PIX"
                        className="w-40 h-40 mx-auto"
                      />
                      {/* Overlay de teste sobre QR Code */}
                      {isTestMode && (
                        <div className="absolute inset-0 flex items-end justify-center pb-1">
                          <span className="bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5 rounded">
                            SANDBOX
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-center gap-2 text-red-400">
                      <Clock className="w-4 h-4" />
                      <span className="font-mono text-lg">{timeLeft}</span>
                      <span className="text-sm">restantes</span>
                    </div>

                    <div className="space-y-2">
                      <p className="text-white/80 text-sm">Código PIX Copia e Cola:</p>
                      <div className="bg-black/50 p-2 rounded border border-white/10 font-mono text-xs text-white/60 break-all max-h-20 overflow-y-auto">
                        {pixData.payload}
                      </div>
                      <Button
                        onClick={handleCopyPixCode}
                        variant="outline"
                        size="sm"
                        className={`w-full transition-all ${
                          copied 
                            ? 'bg-green-600/20 border-green-500 text-green-400' 
                            : 'bg-black border-green-600 text-green-400 hover:bg-green-600/10'
                        }`}
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Copiado!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar Código PIX
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <Alert className="bg-green-900/20 border-green-400/30">
                    <Shield className="h-4 w-4 text-green-400" />
                    <AlertDescription className="text-green-200 text-sm">
                      <strong>Instruções:</strong> Use o app do seu banco para escanear o QR Code ou copie e cole o código PIX. O pagamento será processado instantaneamente.
                    </AlertDescription>
                  </Alert>

                  <Alert className="bg-blue-900/20 border-blue-400/30">
                    <Clock className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-blue-200 text-sm">
                      <strong>Aguardando pagamento...</strong> Assim que o pagamento for confirmado, seu plano será ativado automaticamente.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {pixData && isExpired && (
                <div className="space-y-4 text-center">
                  <Alert className="bg-red-900/20 border-red-400/30">
                    <AlertCircle className="h-4 w-4 text-red-400" />
                    <AlertDescription className="text-red-200">
                      <strong>Código PIX Expirado!</strong><br />
                      O tempo limite de 10 minutos foi atingido. Gere um novo código para continuar.
                    </AlertDescription>
                  </Alert>
                  
                  <Button
                    onClick={() => {
                      setSelectedMethod('PIX');
                      onPixGenerate();
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    disabled={isLoading}
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Gerar Novo Código PIX
                  </Button>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 bg-black border-black text-white hover:bg-black/80"
                >
                  {pixData ? 'Fechar' : 'Cancelar'}
                </Button>
                {!pixData && selectedMethod === 'CREDIT_CARD' && (
                  <Button
                    disabled={true}
                    className="flex-1 bg-gray-600/50 text-white/50 cursor-not-allowed"
                  >
                    Em breve
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};